import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface CoverflowImage {
  src: string;
  title: string;
  subtitle: string;
}

interface CoverflowProps {
  images: CoverflowImage[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

const Coverflow: React.FC<CoverflowProps> = ({
  images,
  autoPlay = true,
  autoPlayInterval = 4000
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(Math.floor(images.length / 2));
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);

  // Configuration
  const spacing = isMobile ? 180 : 280;
  const sideRotation = 55;
  const sideZ = -200;
  const frontZ = 100;
  const itemWidth = isMobile ? 240 : 320;
  const itemHeight = isMobile ? 160 : 213;

  const getTransformValues = useCallback((offset: number) => {
    if (offset === 0) {
      return { x: 0, y: 0, z: frontZ, rotateY: 0, scale: 1 };
    } else {
      return {
        x: offset * spacing,
        y: 0,
        z: sideZ,
        rotateY: offset < 0 ? sideRotation : -sideRotation,
        scale: 1
      };
    }
  }, [spacing, sideRotation, sideZ, frontZ]);

  const buildTransformString = useCallback((t: { x: number; y: number; z: number; rotateY: number; scale: number }) => {
    return `translate(calc(-50% + ${t.x}px), calc(-50% + ${t.y}px)) translateZ(${t.z}px) rotateY(${t.rotateY}deg) scale(${t.scale})`;
  }, []);

  const goTo = useCallback((index: number) => {
    if (index >= 0 && index < images.length) {
      setCurrentIndex(index);
    }
  }, [images.length]);

  const prev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const next = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, images.length]);

  // Auto-play
  useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= images.length - 1) return 0;
        return prev + 1;
      });
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, images.length]);

  // Touch/swipe support
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next();
      else prev();
    }
  }, [next, prev]);

  // Mouse drag support
  const isDragging = useRef(false);
  const startX = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.clientX;
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const diff = startX.current - e.clientX;
    if (Math.abs(diff) > 80) {
      if (diff > 0) next();
      else prev();
      isDragging.current = false;
      startX.current = e.clientX;
    }
  }, [next, prev]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        py: { xs: 4, md: 6 },
        overflow: 'hidden',
      }}
    >
      {/* Coverflow Container */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 280, md: 380 },
          perspective: '1200px',
          perspectiveOrigin: '50% 40%',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
      >
        {/* Stage */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d',
          }}
        >
          {images.map((image, index) => {
            const offset = index - currentIndex;
            const t = getTransformValues(offset);
            const isActive = offset === 0;

            return (
              <Box
                key={index}
                onClick={() => goTo(index)}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: itemWidth,
                  height: itemHeight,
                  transformStyle: 'preserve-3d',
                  cursor: 'pointer',
                  filter: isActive ? 'brightness(1) saturate(1)' : 'brightness(0.5) saturate(0.7)',
                  transform: buildTransformString(t),
                  zIndex: isActive ? 100 : 50 - Math.abs(offset),
                  transition: 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), filter 0.5s ease',
                }}
              >
                {/* Image Wrapper */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: isActive
                      ? '0 35px 80px -20px rgba(0, 0, 0, 0.9), 0 0 100px -20px rgba(102, 126, 234, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      : '0 25px 60px -20px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)',
                    transition: 'box-shadow 0.7s ease',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(165deg, rgba(255, 255, 255, 0.1) 0%, transparent 40%, transparent 60%, rgba(0, 0, 0, 0.2) 100%)',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={image.src}
                    alt={image.title}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>

                {/* Reflection */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '100%',
                    height: '60%',
                    transform: 'scaleY(-1) translateY(-2px)',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    opacity: 0.25,
                    maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 60%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 60%)',
                  }}
                >
                  <Box
                    component="img"
                    src={image.src}
                    alt=""
                    aria-hidden="true"
                    sx={{
                      width: '100%',
                      height: '167%',
                      objectFit: 'cover',
                      filter: 'blur(1px)',
                    }}
                  />
                </Box>

                {/* Caption */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -60,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    textAlign: 'center',
                    opacity: isActive ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box
                    component="h3"
                    sx={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontWeight: 500,
                      fontSize: '1.4rem',
                      color: 'text.primary',
                      mb: 0.5,
                      m: 0,
                    }}
                  >
                    {image.title}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 200,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {image.subtitle}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Navigation Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 3, md: 6 },
          mt: { xs: 6, md: 8 },
        }}
      >
        {/* Previous Button */}
        <IconButton
          onClick={prev}
          disabled={currentIndex === 0}
          sx={{
            width: 56,
            height: 56,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            color: 'text.secondary',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              transform: 'scale(1.05)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '&.Mui-disabled': {
              opacity: 0.3,
            },
          }}
          aria-label="Previous image"
        >
          <ChevronLeft />
        </IconButton>

        {/* Navigation Dots */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={() => goTo(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'primary.main' : 'rgba(240, 236, 226, 0.2)',
                boxShadow: index === currentIndex ? '0 0 10px rgba(102, 126, 234, 0.5)' : 'none',
                transform: index === currentIndex ? 'scale(1.3)' : 'scale(1)',
                transition: 'all 0.4s ease',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: index === currentIndex ? 'primary.main' : 'rgba(240, 236, 226, 0.4)',
                },
              }}
            />
          ))}
        </Box>

        {/* Next Button */}
        <IconButton
          onClick={next}
          disabled={currentIndex === images.length - 1}
          sx={{
            width: 56,
            height: 56,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            color: 'text.secondary',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              transform: 'scale(1.05)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '&.Mui-disabled': {
              opacity: 0.3,
            },
          }}
          aria-label="Next image"
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Coverflow;
